import { Icon, theme } from '@ketch-com/deck'
import { Box, List, ListItem, styled, Typography, Tooltip as MuiTooltip } from '@mui/material'
import React from 'react'
import { Cell, Pie, PieChart, Tooltip } from 'recharts'

export type AuditLogPieChartProps = {
  primaryItems: string[]
  secondaryItems: string[]
  title: string
  description: string
  primaryItemName: string
  secondaryItemName: string
  primaryColor?: string
  tooltip?: string
  width?: number | string
}

const PieChartBox = styled(Box)({
  display: 'flex',
  gap: 8,
})

const PieChartTextBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  alignItems: 'flex-start',
  justifyContent: 'center',
})

const TooltipBox = styled(Box)({
  background: theme.palette.Black.o100,
  borderRadius: '11px',
  padding: 10,
  paddingBottom: 2,
  minWidth: 250,
  display: 'flex',
  flexDirection: 'column',
  position: 'absolute',
})

const TooltipList = styled(List)({
  listStyleType: 'disc',
  marginLeft: 24,
})

const TooltipListItem = styled(ListItem)({
  display: 'list-item',
  color: 'white !important',
  paddingBottom: 2,
  paddingTop: 2,
})

export const AuditLogPieChart: React.FC<AuditLogPieChartProps> = ({
  primaryItems,
  secondaryItems,
  title,
  description,
  primaryItemName,
  secondaryItemName,
  // Using legacy color per design requirements
  primaryColor = theme.palette.persianBlue.main,
  tooltip,
  width,
}) => {
  let percent = (primaryItems.length / (primaryItems.length + secondaryItems.length)) * 100

  if (Number.isNaN(percent)) {
    percent = 0
  }

  const data = [
    {
      percent,
      color: primaryColor,
      items: primaryItems,
      name: primaryItemName,
    },
    {
      percent: 100 - percent,
      color: theme.palette.Black.o16,
      items: secondaryItems,
      name: secondaryItemName,
    },
  ]

  return (
    <PieChartBox width={width}>
      <PieChart width={109} height={110} data={data}>
        <Pie
          data={data}
          animationDuration={500}
          startAngle={90}
          endAngle={-270}
          innerRadius={20}
          outerRadius={50}
          paddingAngle={1}
          dataKey="percent"
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color} />
          ))}
        </Pie>
        <Tooltip
          active
          position={{ x: 0, y: 110 }}
          wrapperStyle={{ zIndex: 1000 }}
          content={props => {
            const items = props.payload?.[0]?.payload?.items

            return (
              <TooltipBox>
                <Typography variant="label" color={theme.palette.Text.PrimaryLight} sx={{ fontWeight: 700 }}>
                  {props.payload?.[0]?.payload?.name}
                </Typography>

                <TooltipList>
                  {items?.map((item: string) => (
                    <TooltipListItem key={item}>
                      <Typography ml={-2}>{item}</Typography>
                    </TooltipListItem>
                  ))}
                </TooltipList>
              </TooltipBox>
            )
          }}
        />
      </PieChart>
      <PieChartTextBox>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography variant="body">{title}</Typography>
          {tooltip ? (
            <MuiTooltip title={tooltip} placement="bottom">
              <span style={{ display: 'flex', height: 'fit-content' }}>
                <Icon name="FUnknown" iconColor={theme.palette.Black.o48} width={16} height={16} />
              </span>
            </MuiTooltip>
          ) : null}
        </Box>
        <Typography variant="h3">{percent.toFixed(0)}%</Typography>
        <Box display="flex" flexDirection="column" gap="2px">
          <Typography variant="smallLabel">
            {primaryItems.length}
            <Typography variant="smallLabel" color={theme.palette.Text.Secondary}>
              /{primaryItems.length + secondaryItems.length}
            </Typography>
          </Typography>
          <Typography variant="smallLabel">{description}</Typography>
        </Box>
      </PieChartTextBox>
    </PieChartBox>
  )
}
